import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_PATH } from '../../consts/app.const';
import { ICreatedModel } from '../common/model/common.model';
import {
  FormBettingOptionalFields,
  ICreateBettingDto,
  ICreatedEntityDto,
  IFormBettingResultsDto,
  IFormBettings,
  IFormBettingStatusesDto,
  IFormDto,
  IFormsDto,
} from '@oogaday/shared';
import { FormBettingResultModel, FormBettingStatusModel, IFormModel } from './model/form.model';
import { UtilsService } from '../../services/utils.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FormBettingApiService {
  private readonly httpClient = inject(HttpClient);


  getForms(compGroupId: string, fields: FormBettingOptionalFields[] = []): Promise<IFormModel[]> {
    return firstValueFrom(this.httpClient.get<IFormsDto>(`${API_PATH}competitionGroups/${compGroupId}/forms`, {
      params: {
        fields,
      },
    }))
      .then(({ forms }) => forms.map((form) => ({
        ...UtilsService.dtoToModel(form),
        startsAt: UtilsService.unixToDate(form.startsAt),
      })));
  }

  getFormById(compGroupId: string, formId: string, fields: FormBettingOptionalFields[] = []): Promise<IFormModel> {
    return firstValueFrom(this.httpClient.get<IFormDto>(`${API_PATH}competitionGroups/${compGroupId}/forms/${formId}`, {
      params: {
        fields,
      },
    }))
      .then((form) => ({
        ...UtilsService.dtoToModel(form),
        startsAt: UtilsService.unixToDate(form.startsAt),
      }));
  }

  createBetting(compGroupId: string, formId: string, data: IFormBettings<any>): Promise<ICreatedModel> {
    return firstValueFrom(this.httpClient.post<ICreatedEntityDto>(`${API_PATH}competitionGroups/${compGroupId}/forms/${formId}/bettings`, {
      betting: data,
    } as ICreateBettingDto<any>));
  }

  updateBetting(compGroupId: string, formId: string, bettingId: string, data: IFormBettings<any>): Promise<void> {
    return firstValueFrom(this.httpClient.put<void>(`${API_PATH}competitionGroups/${compGroupId}/forms/${formId}/bettings/${bettingId}`, {
      betting: data,
    } as ICreateBettingDto<any>));
  }

  getFormResults(compGroupId: string, formId: string): Promise<FormBettingResultModel[]> {
    return firstValueFrom(this.httpClient.get<IFormBettingResultsDto>(`${API_PATH}competitionGroups/${compGroupId}/forms/${formId}/bettings`))
      .then(({ usersBetting }) => usersBetting);
  }

  getFormSubmissionStatus(compGroupId: string, formId: string): Promise<FormBettingStatusModel[]> {
    return firstValueFrom(this.httpClient.get<IFormBettingStatusesDto>(`${API_PATH}competitionGroups/${compGroupId}/forms/${formId}/status`))
      .then(({ statuses }) => statuses);
  }

  simulateBettingResults(compGroupId: string, formId: string, data: IFormBettings<any>): Promise<FormBettingResultModel[]> {
    return firstValueFrom(this.httpClient.post<IFormBettingResultsDto>(`${API_PATH}competitionGroups/${compGroupId}/forms/${formId}/simulateResults`, {
      betting: data,
    } as ICreateBettingDto<any>))
    .then(({ usersBetting }) => usersBetting);
  }
}
